/**
 * Analytics Service
 * Centralized page view and event tracking.
 * All metrics flow through this single module — no duplication across components.
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

export interface PageViewEvent {
  page: string;           // Route path (e.g., "/art", "/art/robots-halloween")
  category?: string;      // Page category (e.g., "art", "fragrance", "body", "home")
  productHandle?: string; // Product handle if viewing a specific product
  referrer?: string;      // Document referrer
  timestamp: string;      // ISO timestamp
}

/**
 * Derive page category and product handle from a pathname.
 * This is the single source of truth for how paths map to metrics.
 */
export function parsePageMetrics(pathname: string): { category: string; productHandle?: string } {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) {
    return { category: 'home' };
  }

  const section = segments[0];

  // Product detail pages: /art/:handle, /fragrance/:handle, /body/:handle
  if (['art', 'fragrance', 'body'].includes(section) && segments.length >= 2) {
    return { category: section, productHandle: segments[1] };
  }

  // Section pages
  const categoryMap: Record<string, string> = {
    art: 'art',
    fragrance: 'fragrance',
    body: 'body',
    music: 'music',
    about: 'about',
    checkout: 'checkout',
    'order-confirmation': 'order-confirmation',
    privacy: 'legal',
    terms: 'legal',
    policies: 'legal',
  };

  return { category: categoryMap[section] || 'other' };
}

/**
 * Send a page view event to the backend.
 * Fire-and-forget — never blocks UI or throws to the caller.
 */
export async function trackPageView(pathname: string): Promise<void> {
  try {
    const { category, productHandle } = parsePageMetrics(pathname);

    const event: PageViewEvent = {
      page: pathname,
      category,
      productHandle,
      referrer: document.referrer || undefined,
      timestamp: new Date().toISOString(),
    };

    // Fire and forget — don't await in calling code
    await fetch(`${API_BASE_URL}/api/analytics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(event),
      keepalive: true, // Ensures request completes even if page navigates away
    });
  } catch {
    // Silent fail — analytics should never break the app
  }
}
